import { get, post, postFull, put, del, getPage } from '@/utils/request'
import { useAuthStore } from '@/stores/auth'
import type {
  UIAILog,
  UIAIUsageSummary,
  UIArtifactFile,
  UICacheKey,
  UIElement,
  UIExcelImportResult,
  UIExecutionDetail,
  UIExecutionSummary,
  UIFlowDefinition,
  UIHealLog,
  UIPage,
  UIPublicModule,
  UIRecorderResult,
  UIRecorderSession,
  UIReview,
  UIScript,
  UISettings,
  UITrendData,
} from '@/types'

/** 脚本创建/更新载荷 */
export interface UIScriptPayload {
  project_id: number
  name: string
  description?: string
  platform?: string
  url?: string
  steps?: UIFlowDefinition
  status?: number
  case_type?: string
  bypass_cache?: boolean
  cache_version?: string
  /** 四期 H：是否开放为公共步骤库模块 */
  is_public?: boolean
  /** 四期 H：公共模块名（is_public=true 时展示名） */
  module_name?: string
}

/** 获取脚本列表 */
export function listScripts(params: {
  project_id: number
  page?: number
  page_size?: number
  keyword?: string
  case_type?: string
  status?: number
}) {
  return getPage<UIScript>('/ui-automation/scripts', params)
}

/** 创建脚本 */
export function createScript(data: UIScriptPayload) {
  return post<UIScript>('/ui-automation/scripts', data)
}

/** 更新脚本 */
export function updateScript(id: number, data: Partial<UIScriptPayload> & { project_id: number }) {
  return put<UIScript>(`/ui-automation/scripts/${id}`, data, { params: { project_id: data.project_id } })
}

/** 删除脚本 */
export function deleteScript(id: number, project_id: number) {
  return del(`/ui-automation/scripts/${id}`, { params: { project_id } })
}

/** 触发脚本执行，返回执行记录 ID */
export function executeScript(id: number, project_id: number, data: { environment_id?: number; bypass_cache?: boolean; headed?: boolean; attach_browser?: boolean; attach_start_url?: string; attach_cdp_port?: number }) {
  return post<{ execution_id: number }>(`/ui-automation/scripts/${id}/execute`, data, { params: { project_id } })
}

/** 试跑（强制绕过 AI 步骤缓存），返回执行记录 ID */
export function dryRunScript(id: number, project_id: number, data: { environment_id?: number; bypass_cache?: boolean; headed?: boolean; attach_browser?: boolean; attach_start_url?: string; attach_cdp_port?: number }) {
  return post<{ execution_id: number }>(`/ui-automation/scripts/${id}/dry-run`, data, { params: { project_id } })
}

/** 一键打开调试浏览器（独立 profile + 远程调试端口启动，用户登录后供接管执行） */
export function openAttachBrowser(port: number) {
  return post<null>('/ui-automation/attach-browser/open', { port })
}

/** 检测 CDP 调试端口是否就绪（打开调试浏览器后轮询，就绪即可登录并接管执行） */
export function checkAttachBrowser(port: number) {
  return get<{ ready: boolean }>('/ui-automation/attach-browser/check', { port })
}

/** 获取元素库页面树（附带每个页面的元素计数） */
export function listPages(project_id: number) {
  return get<UIPage[]>('/ui-automation/pages', { project_id })
}

/** 创建元素库页面 */
export function createPage(data: { project_id: number; name: string; url?: string; description?: string; sort?: number }) {
  return post<UIPage>('/ui-automation/pages', data)
}

/** 更新元素库页面 */
export function updatePage(id: number, data: Partial<{ name: string; url: string; description: string; sort: number }> & { project_id: number }) {
  return put<UIPage>(`/ui-automation/pages/${id}`, data, { params: { project_id: data.project_id } })
}

/** 删除元素库页面（页面下元素自动归入未分组），返回受影响元素数 */
export function deletePage(id: number, project_id: number) {
  return del<{ affected_elements: number }>(`/ui-automation/pages/${id}`, { params: { project_id } })
}

/** 获取元素库列表（page_id>0 时按所属页面过滤） */
export function listElements(params: { project_id: number; page?: number; page_size?: number; keyword?: string; page_id?: number; selector_type?: string }) {
  return getPage<UIElement>('/ui-automation/elements', params)
}

/** 创建元素 */
export function createElement(data: { project_id: number; page_id?: number; name: string; selector: string; selector_type?: string; page_url?: string; description?: string; wait_timeout?: number }) {
  return post<UIElement>('/ui-automation/elements', data)
}

/** 更新元素 */
export function updateElement(id: number, data: Partial<UIElement> & { project_id: number }) {
  return put<UIElement>(`/ui-automation/elements/${id}`, data, { params: { project_id: data.project_id } })
}

/** 删除元素 */
export function deleteElement(id: number, project_id: number) {
  return del(`/ui-automation/elements/${id}`, { params: { project_id } })
}

/** 获取执行记录列表 */
export function listExecutions(params: {
  project_id: number
  script_id?: number
  /** 五期 #6：触发来源筛选（1=手动/2=定时，不传=全部） */
  trigger_source?: number
  page?: number
  page_size?: number
}) {
  return getPage<UIExecutionSummary>('/ui-automation/executions', params)
}

/** 执行引擎 worker 状态（Redis 心跳 + 队列积压数） */
export function getWorkerStatus() {
  return get<{ running: boolean; last_beat: number | null; queue_len: number }>('/ui-automation/worker/status')
}

/** 获取执行详情（含实时日志，供轮询） */
export function getExecution(id: number) {
  return get<UIExecutionDetail>(`/ui-automation/executions/${id}`)
}

/** 获取显性执行实时画面帧（CDP 截屏，无帧返回 image=null，前端执行中 500ms 轮询渲染） */
export function getExecutionFrame(id: number) {
  return get<{ image: string | null }>(`/ui-automation/executions/${id}/frame`)
}

/** 获取执行产物文件列表（截图/trace 等） */
export function listExecutionArtifacts(id: number) {
  return get<UIArtifactFile[]>(`/ui-automation/executions/${id}/artifacts`)
}

// ==================== 二期：批量执行 / 模块设置 / 自愈 / AI 用量 / 复核 / 缓存 ====================

/** 批量触发执行（逐个入队，单条失败不阻塞其余；返回成失败明细） */
export function batchExecuteScripts(data: {
  script_ids: number[]
  environment_id?: number
  bypass_cache?: boolean
  headed?: boolean
}) {
  return post<{ execution_ids: number[]; failed: { script_id: number; reason: string }[] }>(
    '/ui-automation/scripts/batch-execute', data,
  )
}

/** 查询 UI 自动化模块设置（共识断言开关/分歧策略/置信度阈值/副模型配置；API Key 不回传明文） */
export function getUISettings() {
  return get<UISettings>('/ui-automation/settings')
}

/** 更新模块设置（secondary_api_key 传空串表示保持现有密钥不变） */
export function updateUISettings(data: Partial<Omit<UISettings, 'secondary_api_key_set' | 'update_time'>> & { secondary_api_key?: string }) {
  return put<UISettings>('/ui-automation/settings', data)
}

/** 分页查询 AI 步骤自愈记录（SDK-05） */
export function listHealLogs(params: {
  project_id: number
  page?: number
  page_size?: number
  script_id?: number
  status?: string
}) {
  return getPage<UIHealLog>('/ui-automation/heal-logs', params)
}

/** 三期 B：将自愈成功的动作一键沉淀为元素库元素（返回元素与后端 message 提示） */
export async function promoteHealElement(healLogId: number, data: { name: string; page_id?: number; description?: string }) {
  const res = await postFull<UIElement>(`/ui-automation/heal-logs/${healLogId}/promote-element`, data)
  return { element: res.data, message: res.message }
}

/** 三期 D：趋势看板聚合（按日通过率/耗时/置信度/自愈率 + flaky 用例识别） */
export function getTrends(project_id: number, days = 30) {
  return get<UITrendData>('/ui-automation/trends', { project_id, days })
}

/** 分页查询 AI 用量日志（SDK-06：每次真实 LLM 调用一条） */
export function listAILogs(params: {
  project_id: number
  page?: number
  page_size?: number
  script_id?: number
  capability?: string
}) {
  return getPage<UIAILog>('/ui-automation/ai-logs', params)
}

/** AI 用量汇总统计（调用次数/输入输出 Token/缓存命中/自愈次数） */
export function getAIUsageSummary(project_id: number, script_id?: number) {
  return get<UIAIUsageSummary>('/ui-automation/ai-logs/summary', { project_id, script_id: script_id || 0 })
}

/** 分页查询断言复核队列（共识分歧或置信度低于阈值自动入队） */
export function listReviews(params: {
  project_id: number
  page?: number
  page_size?: number
  review_status?: string
  script_id?: number
}) {
  return getPage<UIReview>('/ui-automation/reviews', params)
}

/** 人工复核断言：写入复核结论（pass=确认通过/fail=确认失败/false_positive=误报）与备注 */
export function reviewAssertion(reviewId: number, data: { review_result: 'pass' | 'fail' | 'false_positive'; review_comment?: string }) {
  return post<{ id: number; review_status: string; review_result: string }>(
    `/ui-automation/reviews/${reviewId}/review`, data,
  )
}

/** 分页查看 AI 步骤翻译缓存（后端代理 Redis SCAN，含缓存值摘要） */
export function listCacheKeys(params: { project_id: number; page?: number; page_size?: number; keyword?: string }) {
  return getPage<UICacheKey>('/ui-automation/cache', params)
}

/** 失效 AI 步骤翻译缓存（keys 不传则清空项目全部；FastAPI 重复参数格式手工拼接） */
export function deleteCacheKeys(project_id: number, keys?: string[]) {
  let url = `/ui-automation/cache?project_id=${project_id}`
  if (keys && keys.length) {
    url += keys.map((k) => `&keys=${encodeURIComponent(k)}`).join('')
  }
  return del<{ deleted: number }>(url)
}

// ==================== 四期：公共步骤库 / 录制器 / Excel 导入 ====================

/** 四期 H：公共步骤库模块列表（StepEditor 引用下拉数据源） */
export function listPublicModules(project_id: number) {
  return get<UIPublicModule[]>('/ui-automation/scripts/public-modules', { project_id })
}

/** 四期 I：启动录制浏览器（Playwright codegen 独立进程，关闭浏览器即完成录制） */
export function startRecorder(url: string) {
  return post<UIRecorderSession>('/ui-automation/recorder/start', { url })
}

/** 四期 I：查询录制会话状态（running/done） */
export function getRecorderStatus(sessionId: string) {
  return get<UIRecorderSession>(`/ui-automation/recorder/${sessionId}/status`)
}

/** 四期 I：读取录制结果（原始 codegen 代码 + 解析出的步骤草稿） */
export function getRecorderResult(sessionId: string) {
  return get<UIRecorderResult>(`/ui-automation/recorder/${sessionId}/result`)
}

/** 四期 I：取消录制 */
export function cancelRecorder(sessionId: string) {
  return post<null>(`/ui-automation/recorder/${sessionId}/cancel`, {})
}

/** 四期 J：Excel 用例导入解析（multipart 上传 xlsx） */
export async function importExcelSteps(file: File) {
  const form = new FormData()
  form.append('file', file)
  const data = await post<UIExcelImportResult>('/ui-automation/excel-import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * 四期 J：下载 Excel 导入模板
 * blob 响应不走 axios 拦截器（拦截器会按 JSON code 校验），用 fetch 带 token 直接下载
 */
export async function downloadExcelTemplate() {
  const token = useAuthStore.getState().token
  const resp = await fetch('/api/v1/ui-automation/excel-template', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    throw new Error(`模板下载失败（HTTP ${resp.status}）`)
  }
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'UI用例导入模板.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
